/**
 * Pixel-Proximity Clustering Engine
 *
 * Groups markers that visually overlap on screen (within `radiusPx` pixels of each other).
 * Only markers that are EXPLICITLY grouped together get flagged.
 *
 * @param {Map} markerPool  - Map of key → { marker, ... }
 * @param {object} projection - Google Maps MapCanvasProjection
 * @param {number} radiusPx - pixel radius threshold for grouping (e.g. 36)
 * @param {Set} eligibleKeys - String(key) set of markers that should participate (i.e. not
 *                             filtered out by the caller). Current marker visibility is
 *                             deliberately ignored so clustering never feeds back on itself.
 * @returns {{ clusters: Array<{keys, centroidLat, centroidLng, count}>, clusteredKeys: Set }}
 */
export function computePixelClusters(markerPool, projection, radiusPx, eligibleKeys) {
    if (!projection) return { clusters: [], clusteredKeys: new Set() };

    const radiusSq = radiusPx * radiusPx;

    // Build list of { key, px, py, lat, lng } for every eligible, on-screen marker.
    const items = [];
    markerPool.forEach((entry, key) => {
        const marker = entry?.marker;
        if (!marker) return;
        if (eligibleKeys && !eligibleKeys.has(String(key))) return;

        const pos = marker.getPosition();
        if (!pos) return;
        const pixel = projection.fromLatLngToContainerPixel(pos);
        if (!pixel) return;
        items.push({ key, px: pixel.x, py: pixel.y, lat: pos.lat(), lng: pos.lng() });
    });

    const n = items.length;
    if (n < 2) return { clusters: [], clusteredKeys: new Set() };

    const clusterList = [];

    // Greedy sweep using squared distance for speed
    for (let i = 0; i < n; i++) {
        let merged = false;
        for (let c = 0; c < clusterList.length; c++) {
            const cl = clusterList[c];
            const dx = items[i].px - cl.cx;
            const dy = items[i].py - cl.cy;
            if (dx * dx + dy * dy <= radiusSq) {
                const newCount = cl.keys.length + 1;
                cl.keys.push(items[i].key);
                cl.cx = (cl.cx * (newCount - 1) + items[i].px) / newCount;
                cl.cy = (cl.cy * (newCount - 1) + items[i].py) / newCount;
                cl.latSum += items[i].lat;
                cl.lngSum += items[i].lng;
                merged = true;
                break;
            }
        }
        if (!merged) {
            clusterList.push({
                keys: [items[i].key],
                cx: items[i].px,
                cy: items[i].py,
                latSum: items[i].lat,
                lngSum: items[i].lng,
            });
        }
    }

    const resultClusters = [];
    const newClusteredKeys = new Set();

    clusterList.forEach((cl) => {
        if (cl.keys.length >= 2) {
            cl.keys.forEach(k => newClusteredKeys.add(String(k)));
            resultClusters.push({
                keys: cl.keys,
                count: cl.keys.length,
                centroidLat: cl.latSum / cl.keys.length,
                centroidLng: cl.lngSum / cl.keys.length,
            });
        }
    });

    return { clusters: resultClusters, clusteredKeys: newClusteredKeys };
}
